import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import { 
  User,
  Mail,
  Phone,
  Save,
  Loader2,
  MapPin,
  Briefcase,
  Check,
  Bell,
  Clock,
  Target,
  Globe,
  Plus,
  X,
  Send,
  Zap,
  Play,
  History,
  Trash2,
  AlertTriangle
} from 'lucide-react';

const SHOW_AUTO_APPLY_SETTINGS = false;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', desc: 'Every morning at 8 AM' },
  { value: 'weekly', label: 'Weekly', desc: 'Every Sunday at 9 AM' },
  { value: 'both', label: 'Both', desc: 'Daily + weekly summary' },
  { value: 'none', label: 'Off', desc: 'No email alerts' },
];

const ProfilePage = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    title: '',
    bio: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  
  // Alert preferences
  const [alertPrefs, setAlertPrefs] = useState({
    daily_insights: true,
    weekly_suggestions: true,
    digest_frequency: 'daily',
    min_match_score: 25,
    remote_only: false,
    preferred_locations: [],
    preferred_roles: [],
  });
  const [alertDirty, setAlertDirty] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [newRole, setNewRole] = useState('');

  // Auto-apply state (Unlimited only)
  const [autoApply, setAutoApply] = useState({
    enabled: false,
    min_match_score: 60,
    max_per_run: 10,
    remote_only: false,
    preferred_locations: [],
    preferred_roles: [],
  });
  const [autoApplyDirty, setAutoApplyDirty] = useState(false);
  const [savingAutoApply, setSavingAutoApply] = useState(false);
  const [runningAutoApply, setRunningAutoApply] = useState(false);
  const [autoApplyHistory, setAutoApplyHistory] = useState([]);
  const [newAutoLoc, setNewAutoLoc] = useState('');
  const [newAutoRole, setNewAutoRole] = useState('');
  const isUnlimited = user?.subscription_plan === 'unlimited' || user?.is_founder;

  useEffect(() => {
    fetchProfile();
    fetchEmailPrefs();
    if (user?.subscription_plan === 'unlimited' || user?.is_founder) {
      fetchAutoApplySettings();
      fetchAutoApplyHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmailPrefs = async () => {
    try {
      const res = await axios.get(`${API}/user/email-preferences`, { headers: getAuthHeaders() });
      setAlertPrefs({
        daily_insights: res.data.daily_insights !== false,
        weekly_suggestions: res.data.weekly_suggestions !== false,
        digest_frequency: res.data.digest_frequency || 'daily',
        min_match_score: res.data.min_match_score || 25,
        remote_only: res.data.remote_only || false,
        preferred_locations: res.data.preferred_locations || [],
        preferred_roles: res.data.preferred_roles || [],
      });
    } catch (e) { console.error("Profile fetch error:", e); }
  };

  const saveAlertPrefs = async () => {
    setSavingAlerts(true);
    try {
      await axios.put(`${API}/user/email-preferences`, {
        ...alertPrefs,
        account_notifications: true,
        application_updates: true,
        interview_reminders: true,
      }, { headers: getAuthHeaders() });
      toast.success('Alert preferences saved!');
      setAlertDirty(false);
    } catch { toast.error('Failed to save preferences'); }
    finally { setSavingAlerts(false); }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      // Save prefs first if dirty
      if (alertDirty) await saveAlertPrefs();
      await axios.post(`${API}/recommendations/send-daily-insights`, {}, { headers: getAuthHeaders() });
      toast.success('Test digest email sent! Check your inbox.');
    } catch { toast.error('Failed to send test email'); }
    finally { setSendingTest(false); }
  };

  const updateAlert = (key, value) => {
    setAlertPrefs(prev => ({ ...prev, [key]: value }));
    setAlertDirty(true);
  };

  const addLocation = () => {
    const val = newLocation.trim();
    if (val && !alertPrefs.preferred_locations.includes(val)) {
      updateAlert('preferred_locations', [...alertPrefs.preferred_locations, val]);
      setNewLocation('');
    }
  };

  const removeLocation = (idx) => {
    updateAlert('preferred_locations', alertPrefs.preferred_locations.filter((_, i) => i !== idx));
  };

  const addRole = () => {
    const val = newRole.trim();
    if (val && !alertPrefs.preferred_roles.includes(val)) {
      updateAlert('preferred_roles', [...alertPrefs.preferred_roles, val]);
      setNewRole('');
    }
  };

  const removeRole = (idx) => {
    updateAlert('preferred_roles', alertPrefs.preferred_roles.filter((_, i) => i !== idx));
  };

  // Auto-apply functions
  const fetchAutoApplySettings = async () => {
    try {
      const res = await axios.get(`${API}/auto-apply/settings`, { headers: getAuthHeaders() });
      setAutoApply(res.data);
    } catch (e) { console.error("Profile fetch error:", e); }
  };

  const fetchAutoApplyHistory = async () => {
    try {
      const res = await axios.get(`${API}/auto-apply/history`, { headers: getAuthHeaders() });
      setAutoApplyHistory(res.data.slice(0, 5));
    } catch (e) { console.error("Profile fetch error:", e); }
  };

  const saveAutoApplySettings = async () => {
    setSavingAutoApply(true);
    try {
      await axios.put(`${API}/auto-apply/settings`, autoApply, { headers: getAuthHeaders() });
      toast.success('Auto-apply settings saved!');
      setAutoApplyDirty(false);
    } catch { toast.error('Failed to save'); }
    finally { setSavingAutoApply(false); }
  };

  const runAutoApplyNow = async () => {
    setRunningAutoApply(true);
    try {
      if (autoApplyDirty) await saveAutoApplySettings();
      const res = await axios.post(`${API}/auto-apply/run-now`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to run'); }
    finally { setRunningAutoApply(false); }
  };

  const updateAutoApply = (key, value) => {
    setAutoApply(prev => ({ ...prev, [key]: value }));
    setAutoApplyDirty(true);
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/user/delete-account`, { headers: getAuthHeaders() });
      toast.success('Your account and all data have been permanently deleted.');
      logout();
    } catch {
      toast.error('Failed to delete account. Please try again or contact support.');
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/user/profile`, { headers: getAuthHeaders() });
      setProfile({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        location: res.data.location || '',
        title: res.data.title || '',
        bio: res.data.bio || ''
      });
    } catch (error) {
      if (user) {
        setProfile(prev => ({
          ...prev,
          name: user.name || '',
          email: user.email || ''
        }));
      }
    }
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/profile`, profile, { headers: getAuthHeaders() });
      toast.success('Profile updated successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div data-testid="profile-page" className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-1">Edit Profile</h1>
          <p className="text-zinc-500 mb-8">Update your personal information</p>

          <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft p-6 space-y-6">
            {/* Avatar */}
            <div className="flex items-center gap-4 pb-6 border-b border-zinc-100 dark:border-white/[0.08]">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-2xl">
                {profile.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div>
                <p className="font-heading font-semibold text-zinc-900 dark:text-white text-lg">{profile.name || 'User'}</p>
                <p className="text-sm text-indigo-600 capitalize">{user?.subscription_plan || 'Basic'} Plan</p>
              </div>
            </div>

            {/* Name */}
            <div>
              <Label className="text-zinc-700 font-medium flex items-center gap-2 mb-2">
                <User className="w-4 h-4 text-zinc-400" /> Full Name
              </Label>
              <Input
                value={profile.name}
                onChange={e => handleChange('name', e.target.value)}
                placeholder="Your full name"
                className="rounded-xl"
                data-testid="profile-name-input"
              />
            </div>

            {/* Email (read-only) */}
            <div>
              <Label className="text-zinc-700 font-medium flex items-center gap-2 mb-2">
                <Mail className="w-4 h-4 text-zinc-400" /> Email
              </Label>
              <Input
                value={profile.email}
                disabled
                className="rounded-xl bg-zinc-50 text-zinc-500"
                data-testid="profile-email-input"
              />
              <p className="text-xs text-zinc-400 mt-1">Email cannot be changed</p>
            </div>

            {/* Phone */}
            <div>
              <Label className="text-zinc-700 font-medium flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4 text-zinc-400" /> Phone
              </Label>
              <Input
                value={profile.phone}
                onChange={e => handleChange('phone', e.target.value)}
                placeholder="Your phone number"
                className="rounded-xl"
                data-testid="profile-phone-input"
              />
            </div>

            {/* Location */}
            <div>
              <Label className="text-zinc-700 font-medium flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-zinc-400" /> Location
              </Label>
              <Input
                value={profile.location}
                onChange={e => handleChange('location', e.target.value)}
                placeholder="e.g. Madrid, Spain"
                className="rounded-xl"
                data-testid="profile-location-input"
              />
            </div>

            {/* Job Title */}
            <div>
              <Label className="text-zinc-700 font-medium flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-zinc-400" /> Job Title
              </Label>
              <Input
                value={profile.title}
                onChange={e => handleChange('title', e.target.value)}
                placeholder="e.g. Senior Software Engineer"
                className="rounded-xl"
                data-testid="profile-title-input"
              />
            </div>

            {/* Bio */}
            <div>
              <Label className="text-zinc-700 font-medium mb-2 block">About You</Label>
              <textarea
                value={profile.bio}
                onChange={e => handleChange('bio', e.target.value)}
                placeholder="A short bio about yourself..."
                rows={3}
                className="w-full rounded-xl border border-zinc-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none resize-none"
                data-testid="profile-bio-input"
              />
            </div>

            {/* Daily Alerts Toggle — replaced by full Alert Preferences below */}

            {/* Save Button */}
            <div className="pt-4 border-t border-zinc-100 dark:border-white/[0.08]">
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className="w-full rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white h-11"
                data-testid="profile-save-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : hasChanges ? (
                  <><Save className="w-4 h-4 mr-2" />Save Changes</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Up to Date</>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Job Alert Preferences Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mt-6"
        >
          <h2 className="font-heading text-xl font-bold text-zinc-900 dark:text-white mb-1">Job Alert Preferences</h2>
          <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-4">Control what jobs you receive in your digest emails</p>

          <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft p-6 space-y-6">
            
            {/* Frequency Selector */}
            <div>
              <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-indigo-500" /> Frequency
              </Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="digest-frequency-selector">
                {FREQUENCY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => updateAlert('digest_frequency', opt.value)}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      alertPrefs.digest_frequency === opt.value
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-500/30'
                        : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600'
                    }`}
                    data-testid={`freq-${opt.value}`}
                  >
                    <div className={`text-sm font-semibold ${alertPrefs.digest_frequency === opt.value ? 'text-indigo-600 dark:text-indigo-400' : 'text-zinc-700 dark:text-zinc-300'}`}>{opt.label}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-500 mt-0.5">{opt.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Min Match Score Slider */}
            <div>
              <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-emerald-500" /> Minimum Match Score
              </Label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min={0}
                  max={90}
                  step={5}
                  value={alertPrefs.min_match_score}
                  onChange={e => updateAlert('min_match_score', parseInt(e.target.value))}
                  className="flex-1 accent-indigo-500 h-2 rounded-lg cursor-pointer"
                  data-testid="min-match-slider"
                />
                <span className={`text-sm font-bold min-w-[48px] text-center px-2 py-1 rounded-lg ${
                  alertPrefs.min_match_score >= 80 ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                  alertPrefs.min_match_score >= 50 ? 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400' :
                  'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                }`} data-testid="min-match-value">{alertPrefs.min_match_score}%</span>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">Only include jobs with at least this match percentage</p>
            </div>

            {/* Remote Only Toggle */}
            <div className="flex items-center justify-between py-3 border-t border-zinc-100 dark:border-white/[0.08]">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-blue-500" />
                <div>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium">Remote Jobs Only</span>
                  <p className="text-xs text-zinc-500 dark:text-zinc-500">Only receive remote/worldwide positions</p>
                </div>
              </div>
              <button
                onClick={() => updateAlert('remote_only', !alertPrefs.remote_only)}
                className={`relative w-10 h-5 rounded-full transition-colors ${alertPrefs.remote_only ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'}`}
                data-testid="remote-only-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${alertPrefs.remote_only ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            {/* Preferred Locations */}
            <div className="pt-2">
              <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-orange-500" /> Preferred Locations
              </Label>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-2">Leave empty to use your CV's location. Remote jobs always included.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {alertPrefs.preferred_locations.map((loc, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 px-3 py-1.5 rounded-lg text-sm font-medium group">
                    {loc}
                    <button onClick={() => removeLocation(idx)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-orange-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newLocation}
                  onChange={e => setNewLocation(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addLocation())}
                  placeholder="e.g. Barcelona, Spain..."
                  className="rounded-lg flex-1"
                  data-testid="add-location-input"
                />
                <Button onClick={addLocation} variant="outline" size="sm" className="rounded-lg" data-testid="add-location-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Preferred Roles */}
            <div className="pt-2">
              <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-purple-500" /> Preferred Roles
              </Label>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-2">Leave empty to receive all matching roles</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {alertPrefs.preferred_roles.map((role, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 px-3 py-1.5 rounded-lg text-sm font-medium group">
                    {role}
                    <button onClick={() => removeRole(idx)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-purple-400 hover:text-red-500">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addRole())}
                  placeholder="e.g. Account Executive, Sales..."
                  className="rounded-lg flex-1"
                  data-testid="add-role-input"
                />
                <Button onClick={addRole} variant="outline" size="sm" className="rounded-lg" data-testid="add-role-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Save & Test buttons */}
            <div className="flex gap-3 pt-4 border-t border-zinc-100 dark:border-white/[0.08]">
              <Button
                onClick={saveAlertPrefs}
                disabled={savingAlerts || !alertDirty}
                className="flex-1 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white h-11"
                data-testid="save-alert-prefs-btn"
              >
                {savingAlerts ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                ) : alertDirty ? (
                  <><Save className="w-4 h-4 mr-2" />Save Preferences</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />Saved</>
                )}
              </Button>
              <Button
                onClick={sendTestDigest}
                disabled={sendingTest || alertPrefs.digest_frequency === 'none'}
                variant="outline"
                className="rounded-xl border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-400 h-11"
                data-testid="send-test-digest-btn"
              >
                {sendingTest ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />Send Test Email</>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Auto-Apply Settings — hidden in v1 (no automatic submissions). Code kept for a future release. */}
        {SHOW_AUTO_APPLY_SETTINGS && isUnlimited && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <h2 className="font-heading text-xl font-bold text-zinc-900 dark:text-white mb-1 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" /> Auto-Apply
            </h2>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-4">Automatically apply to top-matching jobs every 4 hours</p>

            <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] shadow-soft p-6 space-y-5">
              
              {/* Enable Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-zinc-700 dark:text-zinc-300 font-semibold">Enable Auto-Apply</span>
                  <p className="text-xs text-zinc-500 dark:text-zinc-500">Runs every 4 hours or on-demand</p>
                </div>
                <button
                  onClick={() => updateAutoApply('enabled', !autoApply.enabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${autoApply.enabled ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-600'}`}
                  data-testid="auto-apply-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${autoApply.enabled ? 'translate-x-6' : ''}`} />
                </button>
              </div>

              {autoApply.enabled && (
                <>
                  {/* Min Match Score */}
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-emerald-500" /> Minimum Match Score
                    </Label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range" min={40} max={95} step={5}
                        value={autoApply.min_match_score}
                        onChange={e => updateAutoApply('min_match_score', parseInt(e.target.value))}
                        className="flex-1 accent-amber-500 h-2 rounded-lg cursor-pointer"
                        data-testid="auto-apply-match-slider"
                      />
                      <span className="text-sm font-bold min-w-[48px] text-center px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400" data-testid="auto-apply-match-value">{autoApply.min_match_score}%</span>
                    </div>
                  </div>

                  {/* Max Per Run */}
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-indigo-500" /> Max Applications Per Run
                    </Label>
                    <div className="flex items-center gap-3">
                      {[5, 10, 20, 50].map(n => (
                        <button
                          key={n}
                          onClick={() => updateAutoApply('max_per_run', n)}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                            autoApply.max_per_run === n
                              ? 'bg-indigo-500 text-white'
                              : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                          }`}
                          data-testid={`max-per-run-${n}`}
                        >{n}</button>
                      ))}
                    </div>
                  </div>

                  {/* Remote Only */}
                  <div className="flex items-center justify-between py-2 border-t border-zinc-100 dark:border-white/[0.08]">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-zinc-700 dark:text-zinc-300 font-medium">Remote Jobs Only</span>
                    </div>
                    <button
                      onClick={() => updateAutoApply('remote_only', !autoApply.remote_only)}
                      className={`relative w-10 h-5 rounded-full transition-colors ${autoApply.remote_only ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'}`}
                      data-testid="auto-apply-remote-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoApply.remote_only ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>

                  {/* Preferred Locations */}
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-orange-500" /> Preferred Locations
                    </Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {autoApply.preferred_locations.map((loc, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 px-3 py-1.5 rounded-lg text-sm font-medium group">
                          {loc}
                          <button onClick={() => updateAutoApply('preferred_locations', autoApply.preferred_locations.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 ml-1 text-orange-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newAutoLoc} onChange={e => setNewAutoLoc(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = newAutoLoc.trim(); if (v) { updateAutoApply('preferred_locations', [...autoApply.preferred_locations, v]); setNewAutoLoc(''); }}}}
                        placeholder="e.g. Barcelona, Spain..." className="rounded-lg flex-1"
                      />
                      <Button onClick={() => { const v = newAutoLoc.trim(); if (v) { updateAutoApply('preferred_locations', [...autoApply.preferred_locations, v]); setNewAutoLoc(''); }}} variant="outline" size="sm" className="rounded-lg"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>

                  {/* Preferred Roles */}
                  <div>
                    <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-2">
                      <Briefcase className="w-4 h-4 text-purple-500" /> Preferred Roles
                    </Label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {autoApply.preferred_roles.map((role, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 px-3 py-1.5 rounded-lg text-sm font-medium group">
                          {role}
                          <button onClick={() => updateAutoApply('preferred_roles', autoApply.preferred_roles.filter((_, i) => i !== idx))} className="opacity-0 group-hover:opacity-100 ml-1 text-purple-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={newAutoRole} onChange={e => setNewAutoRole(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const v = newAutoRole.trim(); if (v) { updateAutoApply('preferred_roles', [...autoApply.preferred_roles, v]); setNewAutoRole(''); }}}}
                        placeholder="e.g. Account Executive, Sales..." className="rounded-lg flex-1"
                      />
                      <Button onClick={() => { const v = newAutoRole.trim(); if (v) { updateAutoApply('preferred_roles', [...autoApply.preferred_roles, v]); setNewAutoRole(''); }}} variant="outline" size="sm" className="rounded-lg"><Plus className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-zinc-100 dark:border-white/[0.08]">
                <Button
                  onClick={saveAutoApplySettings}
                  disabled={savingAutoApply || !autoApplyDirty}
                  className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-600 text-white h-11"
                  data-testid="save-auto-apply-btn"
                >
                  {savingAutoApply ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : autoApplyDirty ? <><Save className="w-4 h-4 mr-2" />Save Settings</> : <><Check className="w-4 h-4 mr-2" />Saved</>}
                </Button>
                <Button
                  onClick={runAutoApplyNow}
                  disabled={runningAutoApply || !autoApply.enabled}
                  variant="outline"
                  className="rounded-xl border-amber-300 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 h-11"
                  data-testid="run-auto-apply-btn"
                >
                  {runningAutoApply ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</> : <><Play className="w-4 h-4 mr-2" />Run Now</>}
                </Button>
              </div>

              {/* Recent History */}
              {autoApplyHistory.length > 0 && (
                <div className="pt-4 border-t border-zinc-100 dark:border-white/[0.08]">
                  <Label className="text-zinc-700 dark:text-zinc-300 font-medium flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-zinc-500" /> Recent Runs
                  </Label>
                  <div className="space-y-2">
                    {autoApplyHistory.map((log, i) => (
                      <div key={log.applied_at || i} className="flex items-center justify-between text-sm bg-zinc-50 dark:bg-zinc-900/50 rounded-lg px-3 py-2">
                        <span className="text-zinc-600 dark:text-zinc-400">
                          {new Date(log.applied_at).toLocaleDateString()} {new Date(log.applied_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{log.applied_count} applied</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Delete My Data Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-10"
        >
          <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-red-200 dark:border-red-500/20 shadow-soft p-6">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <h2 className="font-heading text-lg font-bold text-zinc-900 dark:text-white">Delete My Data</h2>
                <p className="text-zinc-500 dark:text-zinc-400 text-sm mt-1">
                  Permanently delete your account and all associated data including CVs, applications, cover letters, and preferences. This action cannot be undone.
                </p>
              </div>
            </div>

            {!showDeleteConfirm ? (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="mt-2 rounded-xl border-red-300 dark:border-red-500/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account & Data
              </Button>
            ) : (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-500/5 border border-red-200 dark:border-red-500/20 rounded-xl">
                <p className="text-red-700 dark:text-red-300 text-sm font-medium mb-3">
                  Are you absolutely sure? This will permanently delete:
                </p>
                <ul className="text-red-600 dark:text-red-400 text-sm space-y-1 mb-4 pl-4 list-disc">
                  <li>Your account and profile</li>
                  <li>All uploaded CVs and parsed data</li>
                  <li>All job applications and cover letters</li>
                  <li>All preferences and settings</li>
                </ul>
                <div className="flex gap-3">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                    data-testid="confirm-delete-account-btn"
                  >
                    {deletingAccount ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" />Yes, Delete Everything</>
                    )}
                  </Button>
                  <Button
                    onClick={() => setShowDeleteConfirm(false)}
                    variant="outline"
                    className="rounded-xl"
                    data-testid="cancel-delete-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AppLayout>
  );
};

export default ProfilePage;
